import type { TimelineEvent } from "@productlane/api/src/browser"

// Device-info and AI-feedback comments render as system rows, never as comments,
// so they can never join a resolved run.
export function isResolvedCommentEvent(event: TimelineEvent | undefined) {
	if (!event || event.type !== "comment") return false
	if (!event.comment.userId && !event.comment.linearCommentId) return false
	return event.comment.isResolved
}

export function countResolvedComments(events: TimelineEvent[]) {
	return events.reduce((total, event) => {
		if (event.type !== "comment") return total
		return total + 1 + (event.replies?.length ?? 0)
	}, 0)
}

export function resolvedCommentAuthors(events: TimelineEvent[]) {
	const authors = new Set<string>()
	for (const event of events) {
		if (event.type !== "comment") continue
		authors.add(event.comment.userName ?? "Unknown user")
		for (const reply of event.replies ?? []) {
			authors.add(reply.userName ?? "Unknown user")
		}
	}
	return [...authors]
}

export function resolvedCommentsLabel(events: TimelineEvent[]) {
	const count = countResolvedComments(events)
	const noun = count === 1 ? "resolved comment" : "resolved comments"
	const authors = resolvedCommentAuthors(events)
	if (authors.length === 1) return `${count} ${noun} from ${authors[0]}`
	return `${count} ${noun}`
}

export interface ResolvedCommentAuthor {
	key: string
	name: string
	image: string | null
}

// Faces stand in for the check icon on the folded row, so the identities have to
// stay in timeline order and cap before they crowd a 12px row.
export function resolvedCommentAuthorAvatars(
	events: TimelineEvent[],
	max = 3,
): { authors: ResolvedCommentAuthor[]; overflow: number } {
	const seen = new Map<string, ResolvedCommentAuthor>()
	const add = (comment: {
		userName?: string | null
		userImage?: string | null
	}) => {
		const name = comment.userName ?? "Unknown user"
		const image = comment.userImage ?? null
		// Keyed on the face itself: the same person reached through a Linear mirror
		// carries no userId, and two identical avatars read as a bug.
		const key = `${name}|${image ?? ""}`
		if (seen.has(key)) return
		seen.set(key, { key, name, image })
	}
	for (const event of events) {
		if (event.type !== "comment") continue
		add(event.comment)
		for (const reply of event.replies ?? []) add(reply)
	}
	const all = [...seen.values()]
	return { authors: all.slice(0, max), overflow: Math.max(0, all.length - max) }
}
